import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { OrganizationInviteCode } from "@/organization/invite-codes";
import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { OrganizationDataMinimal } from "@/organization/organization";
import { OrganizationMembership } from "@/user/user";
import { users } from "@/api/api";
import { organizations } from "@/organization/api";


export const organizationInviteCodes = createQueryKeys('organizationInviteCodes', {
  list: (organizationId: number) => {
    const queryKey = [organizationId];

    return {
      queryKey,
      queryFn: async () => {
        return await get<PaginatedResponse<OrganizationInviteCode>>(
          `organizations/${organizationId}/invite-codes/`
        );
      },
    };
  },
});

export const useOrganizationInviteCodes = (organizationId: number) => {
  return useSuspenseQuery(organizationInviteCodes.list(organizationId));
};

export const useCreateInviteCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId }: { organizationId: number }) => {
      const res = await apiClient.post(`/organizations/${organizationId}/invite-codes/`, {});
      return res.data;
    },
    onSuccess: () => {
      // Invalidate all organization invite codes queries to refetch the data
      queryClient.invalidateQueries({ queryKey: organizationInviteCodes._def });
    },
  });
};

export const useDeleteInviteCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteCodeId, organizationId }: { inviteCodeId: number; organizationId: number }) => {
      await apiClient.delete(`/organizations/${organizationId}/invite-codes/${inviteCodeId}/`);
    },
    onSuccess: () => {
      // Invalidate all organization invite codes queries to refetch the data
      queryClient.invalidateQueries({ queryKey: organizationInviteCodes._def });
    },
  });
};

export interface AcceptInviteCodeResponse {
  message: string;
  organization: OrganizationDataMinimal;
  membership: OrganizationMembership;
}

export const useAcceptInviteCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string): Promise<AcceptInviteCodeResponse> => {
      const res = await apiClient.post(`/accept-invite-code/`, { code });
      return res.data;
    },
    onSuccess: () => {
      // Invalidate user data and organizations to refetch updated lists
      queryClient.invalidateQueries({ queryKey: users._def });
      queryClient.invalidateQueries({ queryKey: organizations._def });
    },
  });
};
